/**
 * AI rationale drafting (PD-1301) — the framework-agnostic rules, shared by
 * rm-portal and rm-portal-beta so both journeys draft from the same evidence,
 * send the same prompt inputs and record the same provenance.
 *
 * Two sources of truth meet here, deliberately:
 *   · PROMPT SIDE — the developer prompt (rm-rationale-evidence's
 *     rationale_draft_prompt_template.py, now a prompt chain in the DB). It is
 *     evidence-driven: it wants the sub-factor, the level already chosen and a
 *     short list of scored evidence sentences, and it returns
 *     { rationale, cited_sentence_ids, insufficient_evidence }.
 *   · UI SIDE — the Claude Design prototype (lvl/wizard.jsx). Its provenance
 *     rule (`saveR`) and its display strings are reproduced verbatim below.
 *
 * Evidence comes from the AI-assist chain that has already run for the current
 * unit (chain 16/17 for evaluation, chain 11 for classification), so drafting
 * never pays for a second classification pass. Chain 11 is categorical only, so
 * its words are mapped onto the 1-5 scales before ranking.
 */

export const PROVENANCE = Object.freeze({
  ATLAS: 'atlas',
  ATLAS_EDITED: 'atlas-edited',
  USER: 'user',
});

export const DEFAULT_RATIONALE_TOP_K = 5;

/* The feature ships as a beta: the control is badged, and every draft carries
   an oversight note. Shared so both portals and any admin copy say the same. */
export const ATLAS_DRAFT_BETA_LABEL = 'Beta';
export const ATLAS_DRAFT_DISCLAIMER =
  'A starting point for your rationale, not a finished one: check it reflects your own reasoning before you save.';

/* Chain 17's own vocabulary → score, so chain 11 evidence (words only) can be
   ranked with the same combined_score the prompt template expects. */
const ALIGNMENT_SCORES = { direct: 5, focused: 4, partial: 3, incidental: 2, tangential: 1 };
const CENTRALITY_SCORES = { central: 5, dominant: 4, supporting: 3, minor: 2, peripheral: 1 };

function _num(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

function _word(map, v) {
  if (v == null) return null;
  const k = String(v).trim().toLowerCase();
  return Object.prototype.hasOwnProperty.call(map, k) ? map[k] : null;
}

/**
 * Fill in numeric alignment/centrality/combined scores from the categorical
 * words when the numbers are missing. Returns a new object; never mutates.
 */
export function mapCategoricalScores(item) {
  if (!item || typeof item !== 'object') return item;
  const alignment = _num(item.alignment_score) ?? _word(ALIGNMENT_SCORES, item.alignment);
  const centrality = _num(item.centrality_score) ?? _word(CENTRALITY_SCORES, item.centrality);
  const combined = _num(item.combined_score) ?? (alignment != null && centrality != null ? alignment + centrality : null);
  return {
    ...item,
    alignment_score: alignment,
    centrality_score: centrality,
    combined_score: combined,
  };
}

/**
 * Pull the raw evidence array out of an AI-assist unit state — the
 * `factorStates` entry both portals keep per unit:
 * `{ state, result: { outputs: { result } } }`. Tolerates the string/object and
 * `.result.result` nesting the assist composables already tolerate.
 */
export function parseUnitEvidence(unitState) {
  if (!unitState || !unitState.result) return [];
  const outputs = unitState.result.outputs;
  if (!outputs || outputs.result == null) return [];
  let parsed = outputs.result;
  if (typeof parsed === 'string') {
    try {
      parsed = JSON.parse(parsed);
    } catch {
      return [];
    }
  }
  const evidence = (parsed && parsed.result && parsed.result.evidence) || (parsed && parsed.evidence) || [];
  return Array.isArray(evidence) ? evidence : [];
}

/**
 * The evidence the prompt should see for ONE sub-factor: drop the "None"
 * padding and blank sentences, restrict to the sub-factor when one is named
 * (a classification unit covers a whole factor, so pass none there), rank by
 * combined_score and keep the strongest `topK`. Sort is stable, so ties keep
 * the chain's own order.
 */
export function selectEvidence(evidence, { subFactorName = null, topK = DEFAULT_RATIONALE_TOP_K } = {}) {
  const want = subFactorName ? String(subFactorName).trim().toLowerCase() : null;
  const items = (Array.isArray(evidence) ? evidence : [])
    .filter((x) => x && typeof x === 'object')
    .filter((x) => x.sub_factor !== 'None' && String(x.sentence || '').trim())
    .filter((x) => !want || String(x.sub_factor || '').trim().toLowerCase() === want)
    .map(mapCategoricalScores);
  const score = (x) => (x.combined_score == null ? -Infinity : x.combined_score);
  items.sort((a, b) => score(b) - score(a));
  const k = Math.max(0, Number(topK) || DEFAULT_RATIONALE_TOP_K);
  return items.slice(0, k);
}

/**
 * Render evidence in the prompt template's own line format, with LOCAL ids
 * (e1..eN). The chain path has no sentence anchors — those belong to PD-1242 —
 * so the ids only need to be unique within this one request.
 */
export function buildEvidenceText(items) {
  if (!items || !items.length) return '(no evidence provided)';
  return items
    .map((item, i) => {
      const score = item.combined_score == null ? 'n/a' : item.combined_score;
      const sentence = String(item.sentence || '').replace(/\s+/g, ' ').trim();
      return `[e${i + 1}] (combined_score=${score}) "${sentence}"`;
    })
    .join('\n');
}

/* rm-rationale-evidence caps (data_models.py); mirrored so a request is trimmed
   here rather than rejected there with a 422 the evaluator cannot act on. */
export const DRAFT_LIMITS = Object.freeze({
  MAX_EVIDENCE_ITEMS: 100,
  MAX_SENTENCE_CHARS: 2000,
  MAX_SUB_FACTOR_CHARS: 200,
  MAX_SUB_FACTOR_DESCRIPTION_CHARS: 2000,
  MAX_FACTOR_NAME_CHARS: 200,
  MAX_LEVEL_LABEL_CHARS: 200,
  MAX_LEVEL_DEFINITION_CHARS: 4000,
  MAX_LEVEL_NUMBER: 100,
});

const _clip = (v, n) => String(v == null ? '' : v).slice(0, n);

/**
 * The request body for rm-rationale-evidence `POST /draft_rationale`, which the
 * `rationale_draft` prompt chain forwards as a service step. Every field here is
 * one chain input of the same name; the chain overlays them onto the endpoint's
 * body template (see docs/rationale-draft-chain-endpoint.md).
 *
 * The endpoint's contract, honoured here so a request cannot be rejected:
 *   · `sub_factor.name` is required — a classification factor is sent AS the
 *     sub-factor (its name and description), since the prompt is sub-factor
 *     shaped;
 *   · every evidence item's `sub_factor` must equal `sub_factor.name` exactly, so
 *     it is rewritten to that label after selectEvidence's case-insensitive match;
 *   · every item needs integer 1-5 alignment/centrality scores and a resolved
 *     anchor — items whose categorical words could not be mapped are dropped, and
 *     the anchor is a LOCAL id (e1..eN): the endpoint only checks that citations
 *     point at supplied items, not that the id is a document offset;
 *   · `selected_level_number` is an integer >= 1, and the label carries no
 *     terminal punctuation (the draft opens with "<label>." itself).
 *
 * @param {Object} o.factor     { name, description }
 * @param {Object} [o.subFactor] { name, description } — omit for classification
 * @param {Object} o.level      { number, name|label, description }
 * @param {Array}  o.evidence   already selected via selectEvidence()
 */
export function buildDraftInputs({ factor, subFactor = null, level, evidence }) {
  const L = DRAFT_LIMITS;
  const unit = subFactor && subFactor.name ? subFactor : factor || {};
  const subFactorName = _clip(unit.name, L.MAX_SUB_FACTOR_CHARS);
  const items = (Array.isArray(evidence) ? evidence : [])
    .map(mapCategoricalScores)
    .filter((x) => x && Number.isInteger(x.alignment_score) && Number.isInteger(x.centrality_score))
    .slice(0, L.MAX_EVIDENCE_ITEMS)
    .map((x, i) => ({
      sentence_id: `e${i + 1}`,
      anchor_resolved: true,
      ambiguous: false,
      sentence: _clip(String(x.sentence || '').replace(/\s+/g, ' ').trim(), L.MAX_SENTENCE_CHARS),
      sub_factor: subFactorName,
      alignment_score: Math.min(5, Math.max(1, x.alignment_score)),
      centrality_score: Math.min(5, Math.max(1, x.centrality_score)),
      combined_score: Number.isInteger(x.combined_score) ? x.combined_score : x.alignment_score + x.centrality_score,
      justification: x.justification == null ? null : _clip(x.justification, 2000),
    }));

  const number = Number(level && level.number);
  return {
    factor_name: _clip(factor && factor.name, L.MAX_FACTOR_NAME_CHARS),
    sub_factor: {
      name: subFactorName,
      description: _clip(unit.description, L.MAX_SUB_FACTOR_DESCRIPTION_CHARS),
    },
    selected_level_number: Number.isFinite(number) ? Math.min(L.MAX_LEVEL_NUMBER, Math.max(1, Math.round(number))) : 1,
    selected_level_label: _clip((level && (level.label || level.name)) || '', L.MAX_LEVEL_LABEL_CHARS).replace(/[.:;,\s]+$/, ''),
    selected_level_definition: _clip(level && level.description, L.MAX_LEVEL_DEFINITION_CHARS),
    evidence: items,
  };
}

/**
 * The prototype's `saveR` rule, verbatim in intent:
 *   · empty text clears provenance;
 *   · text identical to this session's draft is `atlas`;
 *   · an untouched save keeps what was recorded (a one-word tweak must
 *     downgrade, but re-saving unchanged text must not invent an edit);
 *   · otherwise, if a draft existed this session or the stored value already
 *     came from Atlas, it is `atlas-edited`;
 *   · otherwise `user`.
 */
export function deriveProvenance({ text, draftText = '', savedText = '', savedProvenance = null }) {
  const r = String(text || '').trim();
  if (!r) return null;
  const seed = String(draftText || '').trim();
  const prev = savedProvenance || null;
  if (seed && r === seed) return PROVENANCE.ATLAS;
  const unchanged = r === String(savedText || '').trim();
  if (unchanged && prev) return prev;
  if (seed || isAtlasProvenance(prev)) return PROVENANCE.ATLAS_EDITED;
  return PROVENANCE.USER;
}

export function isAtlasProvenance(provenance) {
  return typeof provenance === 'string' && /^atlas/.test(provenance);
}

/**
 * Display strings from the prototype. `short` is the summary-row variant
 * ("Atlas draft, edited"); the default is the question view ("…, edited by you").
 * Returns null for user-written or absent rationale — nothing is shown then.
 */
export function provenanceLabel(provenance, { short = false } = {}) {
  if (provenance === PROVENANCE.ATLAS) return 'Drafted by Atlas';
  if (provenance === PROVENANCE.ATLAS_EDITED) return short ? 'Atlas draft, edited' : 'Atlas draft, edited by you';
  return null;
}

/* House style on whatever the model returns: em dashes become a comma pause,
   en dashes read as "to". Same post-processing as the prototype. */
export function stripDashes(text) {
  return String(text || '')
    .replace(/\s*—\s*/g, ', ')
    .replace(/\s*–\s*/g, ' to ')
    .trim();
}

/**
 * Read the draft out of the chain's collected step outputs.
 *
 * Understands both shapes the chain can hand back:
 *   · rm-rationale-evidence's `DraftRationaleResponse` (the service-step chain):
 *     `generation_attempted`, `rationale`, `cited_sentence_ids`,
 *     `withheld` + `withheld_reasons`. When withheld, `rationale` is the
 *     service's explanatory MESSAGE, not a draft — never shown as one.
 *   · the prompt template's own structured output (a prompt-step chain):
 *     `rationale`, `cited_sentence_ids`, `insufficient_evidence`.
 * Either may arrive wrapped under `.result` or as a JSON string.
 *
 * Returns `{ rationale, insufficient, attempted, reasons, citedIds }`, or null
 * when nothing usable is there, so a malformed response reads as a failure
 * rather than an empty rationale. `insufficient` is true whenever no draft
 * should be shown: the model declined, the service withheld it, or no
 * generation was attempted; `reasons[0]` says which.
 */
export function parseDraftOutput(outputs) {
  if (!outputs) return null;
  let result = outputs.result != null ? outputs.result : outputs;
  if (typeof result === 'string') {
    try {
      result = JSON.parse(result);
    } catch {
      // A bare string is the rationale itself (a text-output chain).
      const txt = result.trim();
      return txt ? { rationale: txt, insufficient: false, attempted: true, reasons: [], citedIds: [] } : null;
    }
  }
  if (result && result.result && typeof result.result === 'object' && !('rationale' in result)) result = result.result;
  if (!result || typeof result !== 'object') return null;
  const known = ['rationale', 'insufficient_evidence', 'withheld', 'withheld_reasons', 'generation_attempted'];
  if (!known.some((k) => k in result)) return null;

  const attempted = 'generation_attempted' in result ? !!result.generation_attempted : true;
  const reasons = Array.isArray(result.withheld_reasons) ? result.withheld_reasons.map(String) : [];
  const withheld = 'withheld' in result ? !!result.withheld : reasons.length > 0;
  const modelDeclined = !!result.insufficient_evidence;
  if (!attempted) reasons.unshift('no_resolved_evidence');
  else if (modelDeclined && !reasons.length) reasons.push('model_flagged_insufficient');
  const insufficient = !attempted || withheld || modelDeclined;
  return {
    rationale: insufficient ? '' : String(result.rationale || '').trim(),
    insufficient,
    attempted,
    reasons,
    citedIds: Array.isArray(result.cited_sentence_ids) ? result.cited_sentence_ids.slice() : [],
  };
}

/**
 * Whether "Use Atlas" has evidence to draft from, for the unit on screen:
 *   'ready'   — the assist analysis finished and left usable evidence
 *   'empty'   — it finished but found nothing for this sub-factor
 *   'pending' — it is still running (or has not started; the caller should
 *               start it — see rationaleDraftRun's ensureEvidence)
 *   'failed'  — the assist chain failed for this unit
 *   'off'     — drafting is not enabled at all
 * Read inside a computed, the assist state it consults is reactive.
 *
 * @param {Object|null} ctx  { enabled, getEvidence } — the rationaleDraft context
 * @param {string|null} subFactorName  filter for an evaluation unit; null for classification
 */
export function draftEvidenceStatus(ctx, subFactorName = null) {
  if (!ctx || !ctx.enabled) return 'off';
  const ev = ctx.getEvidence ? ctx.getEvidence() : null;
  if (!ev) return 'pending';
  if (ev.failed) return 'failed';
  if (!ev.ready) return 'pending';
  return selectEvidence(ev.items, { subFactorName, topK: 1 }).length ? 'ready' : 'empty';
}

/**
 * The reason "Use Atlas" is disabled, as tooltip copy, or null when it is
 * enabled. Score first (the design's own rule), then evidence readiness, so the
 * evaluator is never offered a click that can only fail.
 */
export function draftDisabledReason({ levelSelected, evidenceStatus }) {
  if (!levelSelected) return 'Select a score first';
  switch (evidenceStatus) {
    case 'pending': return 'Analysing the document…';
    case 'empty': return 'No evidence for this in the document';
    case 'failed': return 'Document analysis failed';
    default: return null;
  }
}

/**
 * A VISIBLE line under the rationale actions explaining why "Use Atlas" is
 * not available yet — a hover tooltip on a disabled button was too easy to
 * miss. Null when there is nothing to explain (ready, or drafting is off).
 * "Select a score first" is deliberately not here: that one stays a tooltip,
 * as in the design.
 */
export function draftGateHint(evidenceStatus) {
  switch (evidenceStatus) {
    case 'pending': return 'Atlas rationale drafting will be available once your document has been analysed.';
    case 'empty': return 'Atlas found no evidence for this question in the document, so there is nothing to draft from.';
    case 'failed': return 'The document analysis failed, so Atlas cannot draft a rationale for this question.';
    default: return null;
  }
}

/** Evaluator-facing copy for a draft that was not produced, keyed on reasons[0]. */
export function draftFailureMessage(reasons) {
  const first = Array.isArray(reasons) && reasons.length ? reasons[0] : null;
  if (first === 'model_flagged_insufficient' || first === 'no_resolved_evidence') {
    return 'Atlas could not find enough in the document to draft this one.';
  }
  return 'Atlas could not draft this one.';
}

/**
 * The three counts the history line reports, from a list of answers carrying
 * `comments` and `rationaleProvenance`. The server computes the persisted
 * counts itself; this is for client-side display and tests.
 */
export function rationaleCounts(answers) {
  const list = Array.isArray(answers) ? answers : [];
  const withText = list.filter((a) => a && String(a.comments || '').trim());
  return {
    rationales: withText.length,
    atlas: withText.filter((a) => a.rationaleProvenance === PROVENANCE.ATLAS).length,
    atlasEdited: withText.filter((a) => a.rationaleProvenance === PROVENANCE.ATLAS_EDITED).length,
  };
}

/** "3 rationales captured · 2 drafted with Atlas (1 edited before accepting)" */
export function formatRationaleCounts({ rationales = 0, atlas = 0, atlasEdited = 0 } = {}) {
  if (!rationales) return null;
  let s = `${rationales} rationale${rationales === 1 ? '' : 's'} captured`;
  const drafted = (atlas || 0) + (atlasEdited || 0);
  if (drafted) {
    s += ` · ${drafted} drafted with Atlas`;
    if (atlasEdited) s += ` (${atlasEdited} edited before accepting)`;
  }
  return s;
}
